"use client";

import { useState, useMemo, useCallback, useEffect, type ReactNode } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import type {
  MountainEntry,
  CoastalEntry,
  ColumnConfig,
  SportConfig,
} from "@/lib/conditions/directory-presentation";
import {
  getSportConfig,
  type SportKey,
} from "@/lib/conditions/directory-presentation";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";
import { SportBetaBadge } from "./SportBetaBadge";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type SortDir = "asc" | "desc";

const PAGE_SIZE = 50;

const NUMERIC_KEYS = new Set([
  "pisteKm",
  "verticalDropM",
  "topAltitudeM",
  "baseElevationM",
  "totalLifts",
  "avgScore",
  "liftPassDailyEur",
  "avgWaterTempC",
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

const ORDINAL_RANK: Record<string, number> = {
  High: 3,
  Mid: 2,
  Low: 1,
  Crowded: 3,
  Moderate: 2,
  Quiet: 1,
  Poor: 1,
  Good: 3,
  Excellent: 4,
  Small: 1,
  Medium: 2,
  Large: 3,
  "Big Wave": 4,
  Green: 1,
  Blue: 2,
  Red: 3,
  Black: 4,
  No: 1,
  Seasonal: 2,
  Yes: 3,
};

function compare(a: unknown, b: unknown, dir: SortDir): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "boolean" && typeof b === "boolean") {
    const na = a ? 1 : 0;
    const nb = b ? 1 : 0;
    return dir === "asc" ? na - nb : nb - na;
  }
  if (typeof a === "number" && typeof b === "number") {
    return dir === "asc" ? a - b : b - a;
  }
  const sa = String(a);
  const sb = String(b);
  const ra = ORDINAL_RANK[sa];
  const rb = ORDINAL_RANK[sb];
  if (ra !== undefined && rb !== undefined) {
    return dir === "asc" ? ra - rb : rb - ra;
  }
  return dir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
}

function reliabilityColor(label: string | null): string {
  switch (label) {
    case "Very Consistent":
      return "text-emerald-700";
    case "Consistent":
      return "text-teal-700";
    case "Variable":
      return "text-amber-700";
    case "Highly Variable":
      return "text-red-700";
    default:
      return "text-text-muted";
  }
}

function badgeColor(value: string | null): string {
  switch (value) {
    case "High":
    case "Crowded":
      return "bg-purple-100 text-purple-800";
    case "Low":
    case "Quiet":
      return "bg-slate-100 text-slate-600";
    default:
      return "bg-amber-50 text-amber-800";
  }
}

function formatCellValue(
  value: unknown,
  format: ColumnConfig["format"],
  key: string
): ReactNode {
  if (value == null || value === "") return <span className="text-text-muted">–</span>;

  switch (format) {
    case "score":
      return scoreOutOf100Label(Number(value));
    case "boolean":
      return (
        <span className={value ? "text-emerald-600" : "text-text-muted"}>
          {value ? "✓" : "–"}
        </span>
      );
    case "temp":
      return `${Math.round(Number(value))}°C`;
    case "number":
      if (key === "liftPassDailyEur") return `€${Number(value).toLocaleString()}`;
      return Number(value).toLocaleString();
    case "badge":
      return (
        <span
          className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-semibold ${badgeColor(String(value))}`}
        >
          {String(value)}
        </span>
      );
    default:
      return String(value);
  }
}

export function normaliseDirectoryClientSport(
  requestedSport: string,
  initialSport: SportKey,
  sportConfigs: readonly SportConfig[],
): SportKey {
  if (sportConfigs.some(({ key }) => key === requestedSport)) {
    return requestedSport as SportKey;
  }
  if (sportConfigs.some(({ key }) => key === initialSport)) return initialSport;
  return sportConfigs[0]?.key ?? "ski";
}

export function directoryEntryHref(slug: string, sport: SportKey): string {
  // Canonical spot URL; a named profile lens rides in the fragment.
  return isSupProfileKey(sport)
    ? `/conditions/${slug}#sport=${sport}`
    : `/conditions/${slug}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  mountain: MountainEntry[];
  coastal: CoastalEntry[];
  sportConfigs: readonly SportConfig[];
  initialSport: SportKey;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DirectoryTable({
  mountain,
  coastal,
  sportConfigs,
  initialSport,
}: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // --- URL state ---
  const sportParam = normaliseDirectoryClientSport(
    searchParams.get("sport") ?? initialSport,
    initialSport,
    sportConfigs,
  );
  const activeConfig = getSportConfig(sportParam, sportConfigs);
  const mountainSports = sportConfigs.filter((sport) => sport.category === "mountain");
  const coastalSports = sportConfigs.filter((sport) => sport.category === "coastal");
  const isMountain = activeConfig.category === "mountain";
  const searchQuery = searchParams.get("q") ?? "";
  const regionFilter = searchParams.get("region") ?? "";
  const countryFilter = searchParams.get("country") ?? "";
  const difficultyFilter = searchParams.get("difficulty") ?? "";
  const treesFilter = searchParams.get("trees") ?? "";
  const sortKey = searchParams.get("sort") ?? "avgScore";
  const sortDir: SortDir = searchParams.get("dir") === "asc" ? "asc" : "desc";
  const page = Math.max(1, Number(searchParams.get("page") ?? 1));

  // --- Column visibility ---
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() =>
    new Set(
      activeConfig.columns
        .filter((c) => c.defaultVisible)
        .map((c) => c.key)
    )
  );
  const [showColumnToggle, setShowColumnToggle] = useState(false);

  // Reset visible columns when sport changes
  useEffect(() => {
    queueMicrotask(() => {
      setVisibleColumns(
        new Set(
          activeConfig.columns
            .filter((c) => c.defaultVisible)
            .map((c) => c.key)
        )
      );
      setShowColumnToggle(false);
    });
  }, [activeConfig]);

  // Close column toggle on outside click
  useEffect(() => {
    if (!showColumnToggle) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-column-toggle]")) {
        setShowColumnToggle(false);
      }
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [showColumnToggle]);

  // --- URL helpers ---
  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = true) => {
      const params = new URLSearchParams(searchParams.toString());
      for (const [key, val] of Object.entries(updates)) {
        if (val === null || val === "") {
          params.delete(key);
        } else {
          params.set(key, val);
        }
      }
      // Remove defaults to keep URL clean
      if (params.get("sport") === "ski") params.delete("sport");
      if (params.get("sort") === "avgScore") params.delete("sort");
      if (params.get("dir") === "desc") params.delete("dir");
      if (resetPage) params.delete("page");
      if (params.get("page") === "1") params.delete("page");
      const qs = params.toString();
      router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
    },
    [searchParams, router, pathname]
  );

  const setSport = useCallback(
    (key: string) => {
      // Reset all filters when switching sport
      const params = new URLSearchParams();
      if (key !== "ski") params.set("sport", key);
      router.replace(`${pathname}${params.toString() ? `?${params}` : ""}`, {
        scroll: false,
      });
    },
    [router, pathname]
  );

  const toggleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        updateParams({ sort: key, dir: sortDir === "desc" ? "asc" : "desc" });
      } else {
        updateParams({
          sort: key,
          dir: NUMERIC_KEYS.has(key) ? "desc" : "asc",
        });
      }
    },
    [sortKey, sortDir, updateParams]
  );

  // --- Derive data ---
  const data = isMountain ? mountain : coastal;

  const regions = useMemo(
    () => [...new Set(data.map((e) => e.region))].sort(),
    [data]
  );

  const countries = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of data) {
      if (!seen.has(e.countryCode)) seen.set(e.countryCode, e.country);
    }
    return [...seen.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([code, name]) => ({ code, name }));
  }, [data]);

  const difficulties = useMemo(() => {
    if (isMountain) return [];
    const set = new Set<string>();
    for (const e of data as CoastalEntry[]) {
      if (e.difficultyLevel) set.add(e.difficultyLevel);
    }
    return [...set].sort();
  }, [data, isMountain]);

  const treeOptions = useMemo(() => {
    if (sportParam !== "off-piste") return [];
    const set = new Set<string>();
    for (const e of data as MountainEntry[]) {
      if (e.treelineTerrain) set.add(e.treelineTerrain);
    }
    // Most-trees-first reads naturally in the dropdown
    const order = ["Extensive", "Limited", "Above treeline"];
    return [...set].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }, [data, sportParam]);

  // --- Filtering and sorting ---
  const filtered = useMemo(() => {
    let items = [...data];

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.country.toLowerCase().includes(q) ||
          e.region.toLowerCase().includes(q)
      );
    }

    // Region filter
    if (regionFilter) {
      items = items.filter((e) => e.region === regionFilter);
    }

    // Country filter
    if (countryFilter) {
      items = items.filter((e) => e.countryCode === countryFilter);
    }

    // Difficulty filter (coastal only)
    if (!isMountain && difficultyFilter) {
      items = (items as CoastalEntry[]).filter(
        (e) => e.difficultyLevel === difficultyFilter
      ) as typeof items;
    }

    // Tree skiing filter (off-piste only)
    if (sportParam === "off-piste" && treesFilter) {
      items = (items as MountainEntry[]).filter(
        (e) => e.treelineTerrain === treesFilter
      ) as typeof items;
    }

    // Sort
    items.sort((a, b) =>
      compare(
        (a as Record<string, unknown>)[sortKey],
        (b as Record<string, unknown>)[sortKey],
        sortDir
      )
    );

    return items;
  }, [data, searchQuery, regionFilter, countryFilter, difficultyFilter, treesFilter, sportParam, sortKey, sortDir, isMountain]);

  const showCount = page * PAGE_SIZE;
  const visible = filtered.slice(0, showCount);
  const hasMore = showCount < filtered.length;

  // Columns to render (always include name, filter rest by visibility)
  const activeColumns = activeConfig.columns.filter(
    (c) => c.key === "name" || visibleColumns.has(c.key)
  );

  const spotLabel = isMountain ? "resorts" : "spots";

  // --- Table header class ---
  const thClass =
    "text-[11px] uppercase tracking-[1px] text-text-muted font-semibold cursor-pointer select-none hover:text-navy transition-colors whitespace-nowrap px-3 py-3";

  return (
    <div>
      {/* Sport tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-none mb-5">
        <div className="flex items-center gap-1 mr-1">
          {mountainSports.map((s: SportConfig) => (
            <button
              key={s.key}
              onClick={() => setSport(s.key)}
              className={`snap-start flex min-h-11 items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors ${
                activeConfig.key === s.key
                  ? "bg-navy text-white"
                  : "bg-warm-bg text-text-muted hover:bg-cream"
              }`}
            >
              <span className="text-[15px]">{s.emoji}</span>
              {s.label}
              {s.isBeta
                ? <SportBetaBadge emphasised={activeConfig.key === s.key} />
                : null}
            </button>
          ))}
        </div>
        <div className="w-px h-5 bg-border-light shrink-0" />
        <div className="flex items-center gap-1 ml-1">
          {coastalSports.map((s: SportConfig) => (
            <button
              key={s.key}
              onClick={() => setSport(s.key)}
              className={`snap-start flex min-h-11 items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors ${
                activeConfig.key === s.key
                  ? "bg-navy text-white"
                  : "bg-warm-bg text-text-muted hover:bg-cream"
              }`}
            >
              <span className="text-[15px]">{s.emoji}</span>
              {s.label}
              {s.isBeta
                ? <SportBetaBadge emphasised={activeConfig.key === s.key} />
                : null}
            </button>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by name, country or region..."
            value={searchQuery}
            onChange={(e) => updateParams({ q: e.target.value || null })}
            className="w-full bg-white border border-border-light rounded-lg px-4 py-2.5 pl-10 text-[14px] text-navy placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40"
            aria-label="Search locations"
          />
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted/50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Region */}
        {regions.length > 1 && (
          <select
            value={regionFilter}
            onChange={(e) => updateParams({ region: e.target.value || null })}
            className="bg-white border border-border-light rounded-lg px-4 py-2.5 text-[14px] text-navy font-medium focus:outline-none focus:ring-2 focus:ring-gold/30 min-w-[160px]"
            aria-label="Filter by region"
          >
            <option value="">All regions</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        )}

        {/* Country */}
        {countries.length > 1 && (
          <select
            value={countryFilter}
            onChange={(e) => updateParams({ country: e.target.value || null })}
            className="bg-white border border-border-light rounded-lg px-4 py-2.5 text-[14px] text-navy font-medium focus:outline-none focus:ring-2 focus:ring-gold/30 min-w-[140px]"
            aria-label="Filter by country"
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {/* Difficulty (coastal only) */}
        {!isMountain && difficulties.length > 1 && (
          <select
            value={difficultyFilter}
            onChange={(e) =>
              updateParams({ difficulty: e.target.value || null })
            }
            className="bg-white border border-border-light rounded-lg px-4 py-2.5 text-[14px] text-navy font-medium focus:outline-none focus:ring-2 focus:ring-gold/30 min-w-[140px]"
            aria-label="Filter by difficulty"
          >
            <option value="">All difficulties</option>
            {difficulties.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}

        {/* Tree skiing (off-piste only) */}
        {sportParam === "off-piste" && treeOptions.length > 1 && (
          <select
            value={treesFilter}
            onChange={(e) => updateParams({ trees: e.target.value || null })}
            className="bg-white border border-border-light rounded-lg px-4 py-2.5 text-[14px] text-navy font-medium focus:outline-none focus:ring-2 focus:ring-gold/30 min-w-[140px]"
            aria-label="Filter by tree skiing"
          >
            <option value="">All tree skiing</option>
            {treeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}

        {/* Column toggle */}
        <div className="relative" data-column-toggle>
          <button
            onClick={() => setShowColumnToggle((v) => !v)}
            className={`flex items-center justify-center w-[42px] h-[42px] rounded-lg border transition-colors ${
              showColumnToggle
                ? "bg-navy text-white border-navy"
                : "bg-white text-text-muted border-border-light hover:border-navy/20 hover:text-navy"
            }`}
            aria-label="Toggle columns"
          >
            <svg
              className="w-4.5 h-4.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
          </button>
          {showColumnToggle && (
            <div className="absolute right-0 top-full mt-2 z-30 bg-white border border-border-light rounded-xl shadow-lg p-3 min-w-[200px] max-h-[320px] overflow-y-auto">
              <p className="text-[11px] uppercase tracking-[1px] text-text-muted font-semibold mb-2 px-1">
                Columns
              </p>
              {activeConfig.columns
                .filter((c) => c.key !== "name")
                .map((c) => (
                  <label
                    key={c.key}
                    className="flex items-center gap-2 px-1 py-1.5 rounded hover:bg-warm-bg/50 cursor-pointer text-[13px] text-navy"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(c.key)}
                      onChange={() => {
                        setVisibleColumns((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.key)) next.delete(c.key);
                          else next.add(c.key);
                          return next;
                        });
                      }}
                      className="rounded border-border-light text-gold focus:ring-gold/30"
                    />
                    {c.label}
                  </label>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Count */}
      <p className="text-[13px] text-text-muted mb-3">
        Showing {Math.min(showCount, filtered.length)} of {filtered.length}{" "}
        {activeConfig.label.toLowerCase()} {spotLabel}
      </p>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-[14px] border border-border-light p-8 text-center">
          <p className="text-[15px] text-text-muted">
            No locations match your filters.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-[14px] border border-border-light overflow-hidden">
          <div className="overflow-x-auto scrollbar-none">
            <table className="w-full text-[13px] min-w-[780px]">
              <thead>
                <tr className="border-b border-border-light">
                  {activeColumns.map((col) =>
                    col.key === "name" ? (
                      <th
                        key={col.key}
                        className="text-left px-3 py-3 text-[11px] uppercase tracking-[1px] text-text-muted font-semibold sticky left-0 bg-white z-10 min-w-[180px]"
                      >
                        {col.sortable ? (
                          <button
                            onClick={() => toggleSort(col.key)}
                            className="hover:text-navy transition-colors"
                          >
                            {isMountain ? "Resort" : "Spot"}
                            {sortKey === col.key && (
                              <span className="ml-0.5" aria-hidden>
                                {sortDir === "desc" ? "▼" : "▲"}
                              </span>
                            )}
                          </button>
                        ) : (
                          isMountain ? "Resort" : "Spot"
                        )}
                      </th>
                    ) : (
                      <th
                        key={col.key}
                        className={`${col.align === "left" ? "text-left" : col.align === "center" ? "text-center" : "text-right"} ${thClass}`}
                        onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                        aria-sort={
                          sortKey === col.key
                            ? sortDir === "desc"
                              ? "descending"
                              : "ascending"
                            : "none"
                        }
                      >
                        {col.label}
                        {sortKey === col.key && (
                          <span className="ml-0.5" aria-hidden>
                            {sortDir === "desc" ? "▼" : "▲"}
                          </span>
                        )}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {visible.map((entry) => (
                  <tr
                    key={entry.slug}
                    className="border-b border-border-light last:border-b-0 hover:bg-warm-bg/50 transition-colors"
                  >
                    {activeColumns.map((col) => {
                      if (col.key === "name") {
                        return (
                          <td
                            key={col.key}
                            className="px-3 py-3 sticky left-0 bg-white z-10"
                          >
                            <Link
                              href={directoryEntryHref(entry.slug, activeConfig.key)}
                              className="no-underline group"
                            >
                              <span className="font-serif text-[14px] font-semibold text-navy group-hover:text-gold transition-colors">
                                {countryFlag(entry.countryCode)} {entry.name}
                              </span>
                              <span className="block text-[11px] text-text-muted mt-0.5">
                                {entry.region}
                              </span>
                            </Link>
                          </td>
                        );
                      }

                      const value = (entry as Record<string, unknown>)[col.key];
                      const align =
                        col.align === "left"
                          ? "text-left"
                          : col.align === "center"
                            ? "text-center"
                            : "text-right";

                      // Special rendering for reliabilityLabel
                      if (col.key === "reliabilityLabel") {
                        return (
                          <td key={col.key} className={`${align} px-3 py-3`}>
                            {value ? (
                              <span
                                className={`text-[12px] font-semibold whitespace-nowrap ${reliabilityColor(String(value))}`}
                              >
                                {String(value)}
                              </span>
                            ) : (
                              <span className="text-text-muted">–</span>
                            )}
                          </td>
                        );
                      }

                      return (
                        <td
                          key={col.key}
                          className={`${align} px-3 py-3 ${
                            col.format === "score" ||
                            col.format === "number" ||
                            col.format === "temp"
                              ? "font-serif tabular-nums text-navy"
                              : col.format === "boolean"
                                ? ""
                                : "text-[12px] text-navy"
                          } ${col.format !== "badge" ? "whitespace-nowrap" : ""}`}
                        >
                          {formatCellValue(value, col.format, col.key)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div className="text-center mt-5">
          <button
            onClick={() =>
              updateParams({ page: String(page + 1) }, false)
            }
            className="bg-white border border-border-light rounded-lg px-6 py-2.5 text-[13px] font-semibold text-navy hover:border-gold/40 hover:text-gold transition-all"
          >
            Show more ({filtered.length - showCount} remaining)
          </button>
        </div>
      )}
    </div>
  );
}
